import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import { AnalysisReportPdfFooter } from "src/core/components/folders-list/pdf/AnalysisReportPdfFooter";
import { CardsGrid } from "src/core/components/folders-list/pdf/CardGrid";
import { FoldersTable } from "src/core/components/folders-list/pdf/FoldersTable";
import { PdfHeader } from "src/core/components/pdf/shared/PdfHeader";
import { PdfPage } from "src/core/components/pdf/shared/PdfPage";
import { User } from "src/core/gql/graphql";
import { ValidatedFoldersStatistics } from "src/core/hooks/folders-list/folder-risk-statistics/types";

const FIRST_PAGE_ITEMS = 12;
const ITEMS_PER_PAGE = 21;

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
  },

  titleRow: {
    marginTop: 36,
    marginBottom: 12,
  },

  subTitleRow: {
    marginTop: 8,
    marginBottom: 0,
  },

  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#0A1A53",
  },
  subTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#0A1A53",
  },

  // Petite marge pour le tableau dans le cas où il est sous un titre.
  foldersTableWrapperFirstPage: {
    marginTop: 12,
  },
  // Plus grande marge pour le tableau s'il est tout seul dans la page
  // (pour ne pas avoir de superposition avec le header).
  foldersTableWrapperNextPages: {
    marginTop: 36,
  },
});

type AnalysisReportTableSectionProps = {
  currentUser: User;
  statistics: ValidatedFoldersStatistics;
};

/**
 * Section "Table" du rapport PDF.
 *
 * - Afficher les titres de section (Row)
 * - Afficher une grille de cartes (CardsGrid) en 3 colonnes max
 * - Afficher le tableau des dossiers :
 *   - 14 premiers dossiers sur la 1ère page, sous le 2ème sous-titre
 *   - puis 22 dossiers par page pour le reste
 * - Afficher le header/footer du PDF
 */
export const AnalysisReportTableSection = ({
  currentUser,
  statistics,
}: AnalysisReportTableSectionProps) => {
  const { t } = useTranslation();

  const folders = statistics.dossiersComplets ?? [];

  // Page 1 : 14 premiers dossiers
  const firstPageFolders = folders.slice(0, FIRST_PAGE_ITEMS);

  // Pages suivantes : le reste, paginé par 22
  const remainingFolders = folders.slice(FIRST_PAGE_ITEMS);
  const remainingPages = chunkArray(remainingFolders, ITEMS_PER_PAGE);

  return (
    <>
      <PdfPage>
        <PdfHeader />

        <Row text={t("pdf.validatedFolders.sections.table.title")} variant="title" />
        <Row
          text={t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.subTitle")}
          variant="subTitle"
        />

        <CardsGrid statistics={statistics} />

        <Row
          text={t("pdf.validatedFolders.sections.table.foldersList.subTitle")}
          variant="subTitle"
        />

        {/* Tableau sous le 2ème sous-titre : 14 premiers dossiers */}
        <View style={styles.foldersTableWrapperFirstPage}>
          <FoldersTable folders={firstPageFolders} />
        </View>

        <AnalysisReportPdfFooter currentUser={currentUser} />
      </PdfPage>

      {/* Pages suivantes : 22 dossiers par nouvelle page */}
      {remainingPages.map((foldersForPage, pageIndex) => (
        <PdfPage key={`folders-page-${pageIndex}`}>
          <PdfHeader />
          <View style={styles.foldersTableWrapperNextPages}>
            <FoldersTable folders={foldersForPage} />
          </View>
          <AnalysisReportPdfFooter currentUser={currentUser} />
        </PdfPage>
      ))}
    </>
  );
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/**
 * Découpe un tableau en pages de taille fixe.
 */
const chunkArray = <T,>(items: readonly T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [];

  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    pages.push(items.slice(i, i + chunkSize));
  }
  return pages;
};

// ----------------------------------------------------------------------------
// Components
// ----------------------------------------------------------------------------

type RowProps = {
  text: string;
  variant: "title" | "subTitle";
};

/**
 * Affiche une ligne de texte avec un titre ou un sous-titre.
 */
const Row = ({ text, variant }: RowProps) => {
  const rowStyle = variant === "title" ? styles.titleRow : styles.subTitleRow;
  const textStyle = variant === "title" ? styles.title : styles.subTitle;

  return (
    <View style={[styles.headerRow, rowStyle]}>
      <Text style={textStyle}>{text}</Text>
    </View>
  );
};
